import React, { useEffect, useImperativeHandle, forwardRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ChordMark } from "./extensions/ChordMark";
import { isChordPattern } from "./hymnHtml";
import "./HymnEditor.css";

const HymnEditor = forwardRef(function HymnEditor(
  { initialHtml = "", onChange },
  ref
) {
  const editor = useEditor({
    extensions: [StarterKit, ChordMark],
    content: initialHtml || "<p></p>",
    onUpdate: ({ editor }) => {
      if (onChange) onChange(editor.getHTML());
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      getHTML: () => (editor ? editor.getHTML() : ""),
      setHTML: (html) => {
        if (editor) editor.commands.setContent(html || "<p></p>");
      },
    }),
    [editor]
  );

  useEffect(() => {
    if (editor && initialHtml) {
      editor.commands.setContent(initialHtml);
    }
    // Deps: [editor] only — intentionally excludes initialHtml so a parent re-render
    // doesn't overwrite edits the user has already made. To load new content
    // programmatically from outside, call the exposed setHTML() via ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (!editor) return null;

  const toggleChord = () => editor.chain().focus().toggleMark("chord").run();

  const insertChordLine = () => {
    editor
      .chain()
      .focus()
      .insertContentAt(editor.state.selection.$from.before(), {
        type: "paragraph",
        content: [
          {
            type: "text",
            marks: [{ type: "chord" }, { type: "bold" }],
            text: " ",
          },
        ],
      })
      .run();
  };

  const clearFormatting = () =>
    editor.chain().focus().unsetAllMarks().run();

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    setTimeout(() => {
      const { state } = editor;
      const { $from } = state.selection;
      const beforeCurrent = $from.before($from.depth);
      if (beforeCurrent <= 0) return;
      const $prev = state.doc.resolve(beforeCurrent - 1);
      if ($prev.depth < $from.depth) return;
      const prevNode = $prev.node($from.depth);
      if (!prevNode || prevNode.type.name !== "paragraph") return;
      const text = prevNode.textContent;
      if (!text.trim()) return;
      let alreadyChord = false;
      prevNode.descendants((child) => {
        if (child.marks?.some((m) => m.type.name === "chord")) {
          alreadyChord = true;
        }
      });
      if (alreadyChord) return;
      if (isChordPattern(text)) {
        const from = $prev.start($from.depth);
        const to = from + prevNode.content.size;
        editor
          .chain()
          .setTextSelection({ from, to })
          .setMark("chord")
          .setMark("bold")
          .setTextSelection($from.pos)
          .run();
      }
    }, 0);
  };

  return (
    <div className="hymn-editor-wrapper">
      <div className="hymn-editor-toolbar">
        <button
          type="button"
          className={editor.isActive("chord") ? "is-active" : ""}
          onClick={toggleChord}
          title="Συγχορδία (Ctrl/Cmd+R)"
        >
          🎵 Συγχορδία
        </button>
        <button type="button" onClick={insertChordLine} title="Νέα γραμμή συγχορδιών">
          + Γραμμή συγχορδιών
        </button>
        <span className="sep" />
        <button
          type="button"
          className={editor.isActive("bold") ? "is-active" : ""}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold (Ctrl/Cmd+B)"
        >
          <b>B</b>
        </button>
        <button
          type="button"
          className={editor.isActive("italic") ? "is-active" : ""}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic (Ctrl/Cmd+I)"
        >
          <i>I</i>
        </button>
        <span className="sep" />
        <button type="button" onClick={clearFormatting} title="Καθαρισμός μορφοποίησης">
          ✕ Καθαρισμός
        </button>
        <span className="sep" />
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl/Cmd+Z)"
        >
          ↶
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl/Cmd+Shift+Z)"
        >
          ↷
        </button>
      </div>
      <div className="hymn-editor-area" onKeyDown={handleKeyDown}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
});

export default HymnEditor;
